import { apiClient } from './api';

// VERACODE-MITIGATION: CWE-918 - All API requests use the environment-configured base URL
// (VITE_API_BASE_URL) via the shared API client. URLs are not user-controlled.

export type TripPurpose =
  | 'BUSINESS'
  | 'BOARD'
  | 'CONFERENCE'
  | 'PERSONAL'
  | 'GOVERNMENT'
  | 'MEDIA'
  | 'OTHER';

export type TripClassification = 'INTERNAL' | 'CONFIDENTIAL' | 'RESTRICTED';

export type BriefStatus = 'DRAFT' | 'GENERATING' | 'READY' | 'APPROVED' | 'ARCHIVED';

export type GroundTransportMode =
  | 'HIRED_CAR'
  | 'RIDESHARE'
  | 'RENTAL'
  | 'TAXI'
  | 'PUBLIC_TRANSIT'
  | 'CLIENT_PROVIDED'
  | 'OTHER';

export type RiskRating = 'LOW' | 'MEDIUM' | 'HIGH';

export type AiSummaryStatus = 'PENDING' | 'READY' | 'FAILED' | 'UNAVAILABLE';
export interface AiSummarySource {
  label: string;
  url: string;
}

export interface ItineraryLeg {
  id: string;
  city: string;
  country: string;
  /** First-order subdivision (US state / province) — disambiguates geocoding. */
  region?: string;
  startDate: string;
  endDate: string;
  notes?: string;
}

export interface FlightSegment {
  id: string;
  carrier: string;
  flightNumber: string;
  departureAirport: string;
  arrivalAirport: string;
  departureTime: string;
  arrivalTime: string;
}

export interface MeetingVenue {
  id: string;
  name: string;
  address: string;
  scheduledAt: string;
  notes?: string;
}

export interface DiningReservation {
  id: string;
  name: string;
  address: string;
  scheduledAt: string;
  notes?: string;
}

export interface Accommodation {
  id: string;
  name: string;
  address: string;
  checkIn: string;
  checkOut: string;
  aliasBooking?: boolean;
  loyaltyProgram?: string;
}

export interface TripLogistics {
  departureAirport?: string;
  arrivalAirport?: string;
  flights?: FlightSegment[];
  groundTransportMode?: GroundTransportMode;
  groundTransportNotes?: string;
  accommodations?: Accommodation[];
  meetings?: MeetingVenue[];
  dining?: DiningReservation[];
}

export interface SpecialConsiderations {
  lgbtqTraveler?: boolean;
  disabilityAccommodations?: string;
  soloFemaleTraveler?: boolean;
  medicalConditions?: string;
  dietaryRequirements?: string;
  securityDetail?: 'NONE' | 'COVERT' | 'OVERT';
  publicFacingProfile?: boolean;
  additionalNotes?: string;
}

export interface BriefSourceCitation {
  id: string;
  label: string;
  url?: string;
  retrievedAt: string;
}

export interface BriefSection {
  key: string;
  order: number;
  title: string;
  riskRating?: RiskRating;
  summary: string;
  body: string;
  bullets?: string[];
  sources: BriefSourceCitation[];
  freshnessTimestamp: string;
  needsReview?: boolean;
  edited?: boolean;
  dataStatus?: 'pending' | 'live' | 'fallback';
  dataSource?: string;
}

export interface MedicalFacility {
  name: string;
  type: 'HOSPITAL' | 'CLINIC' | 'TRAUMA_CENTER' | 'EMBASSY_MEDICAL';
  address: string;
  phone?: string;
  city: string;
  country: string;
  capabilities: string[];
  distanceKm?: number;
  notes?: string;
}

export interface TravelerTip {
  category: 'CULTURAL' | 'LEGAL' | 'HEALTH' | 'SECURITY' | 'DIGITAL';
  title: string;
  body: string;
}

export type AiRecommendation = 'GO' | 'GO_WITH_CAUTION' | 'NO_GO';

export interface FlashCoverHeaderItem {
  label: string;
  value: string;
}

export interface FlashTopRisk {
  area: string;
  rating: RiskRating;
  note: string;
}

export interface FlashItineraryRow {
  city: string;
  country: string;
  dates: string;
}

export interface FlashContact {
  label: string;
  value: string;
  detail?: string;
}

/** Structured Executive Flash cover (VIS-144), derived server-side. */
export interface FlashCover {
  title: string;
  protectedPersonName: string;
  tripWindow: string;
  destinations: string;
  overallRisk: RiskRating;
  recommendation: AiRecommendation | null;
  recommendationRationale: string | null;
  header: FlashCoverHeaderItem[];
  topRisks: FlashTopRisk[];
  itinerary: FlashItineraryRow[];
  emergencyProtocol: string[];
  keyContacts: FlashContact[];
}

export interface TripBriefRecord {
  id: string;
  briefNumber: string;
  title: string;
  protectedPersonId: string;
  protectedPersonName: string;
  purpose: TripPurpose;
  classification: TripClassification;
  requestingParty: string | null;
  nationality: string | null;
  executiveIndustry: string | null;
  internalId: string | null;
  departDate: string;
  returnDate: string;
  briefNeededByDate: string | null;
  itinerary: ItineraryLeg[];
  logistics: TripLogistics | null;
  specialConsiderations: SpecialConsiderations | null;
  travelerNotes: string | null;
  flashSummary: string;
  flashCover: FlashCover;
  aiSummaryStatus: AiSummaryStatus;
  aiSummarySources: AiSummarySource[];
  aiSummaryGeneratedAt: string | null;
  aiSummaryEdited: boolean;
  aiRecommendation: string | null;
  aiRecommendationRationale: string | null;
  liveDataStatus: string;
  liveDataGeneratedAt: string | null;
  sections: BriefSection[];
  medicalFacilities: MedicalFacility[];
  travelerTips: TravelerTip[];
  status: BriefStatus;
  version: number;
  internalNotes: string | null;
  approvedById: string | null;
  approvedByName: string | null;
  approvedByTitle: string | null;
  approvedAt: string | null;
  lastRefreshedAt: string | null;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * True when a freshly-polled brief differs from the one currently rendered and
 * should replace it. During live sourcing the top-level `liveDataStatus` stays
 * 'pending' the whole time while each section's `dataStatus` flips one at a time,
 * so gating only on the status enums froze the "N/16 sourced" progress until a
 * manual refresh. Each incremental server merge bumps `updatedAt`, so keying on
 * it (plus the enums) propagates every section completion and the live-weather
 * rescore. (VIS-207 follow-up.)
 */
export function briefLiveProgressChanged(prev: TripBriefRecord, next: TripBriefRecord): boolean {
  return (
    next.updatedAt !== prev.updatedAt ||
    next.aiSummaryStatus !== prev.aiSummaryStatus ||
    next.liveDataStatus !== prev.liveDataStatus
  );
}

export interface CreateTripBriefInput {
  protectedPersonId: string;
  nationality?: string;
  executiveIndustry?: string;
  internalId?: string;
  title: string;
  purpose: TripPurpose;
  classification: TripClassification;
  requestingParty?: string;
  departDate: string;
  returnDate: string;
  briefNeededByDate?: string;
  itinerary: ItineraryLeg[];
  logistics?: TripLogistics;
  specialConsiderations?: SpecialConsiderations;
  travelerNotes?: string;
}

export interface UpdateTripBriefInput {
  title?: string;
  internalNotes?: string;
  flashSummary?: string;
  sections?: BriefSection[];
  travelerTips?: TravelerTip[];
  medicalFacilities?: MedicalFacility[];
}

export interface TripBriefVersionSummary {
  id: string;
  version: number;
  changeType: string;
  createdAt: string;
  createdByName: string | null;
}

/** VIS-207: all 11 weighted risk dimensions, in canonical display order. */
export const SENSITIVITY_METRIC_KEYS = [
  'country', 'crime', 'terrorism', 'political', 'conflict', 'kidnapping', 'health', 'traffic', 'environmental', 'cyber', 'visa',
] as const;
export type SensitivityMetricKey = (typeof SENSITIVITY_METRIC_KEYS)[number];

/** Allowed sensitivity weight steps (0 = metric excluded from scoring). */
export const SENSITIVITY_WEIGHT_STEPS = [0, 0.5, 1, 1.5, 2] as const;

export type SensitivityWeights = Record<SensitivityMetricKey, number>;

export interface TravelRiskSettings {
  metricWeights: SensitivityWeights;
  updatedById: string | null;
  updatedByName: string | null;
  updatedAt: string | null;
}

/**
 * Shape returned by `list()`. The server projects to exactly these columns so the
 * list response stays small — it deliberately omits `sections`, `flashCover`,
 * `medicalFacilities`, `travelerTips`, `aiSummarySources`, `itinerary`,
 * `logistics` and `specialConsiderations`. Open a single brief via `getById` when
 * you need those.
 */
export interface TripBriefSummary {
  id: string;
  briefNumber: string;
  title: string;
  protectedPersonId: string;
  protectedPersonName: string;
  purpose: TripPurpose | string;
  classification: TripClassification | string;
  status: BriefStatus;
  version: number;
  departDate: string;
  returnDate: string;
  flashSummary: string;
  liveDataStatus: string;
  aiSummaryStatus: AiSummaryStatus;
  createdAt: string;
  updatedAt: string;
}

const BASE = '/api/v1/executive-protection/trip-briefs';
const SETTINGS_BASE = '/api/v1/executive-protection/travel-risk-settings';

/**
 * Unwrap an ApiResponse, throwing the server's error message on failure.
 *
 * `apiClient.request()` never throws — it converts every transport and HTTP
 * error (429, 503, 504, network drop) into `{ success: false, error }`. The read
 * paths below used to turn that into a benign empty value, which made an outage
 * indistinguishable from "no records exist": a rate-limited or 503'd brief list
 * rendered as "No trip briefs yet", i.e. it looked like the trip had never
 * saved. Throw instead so react-query surfaces `isError` and the UI can tell
 * "nothing here" apart from "we couldn't ask".
 *
 * Note `data` is checked for null/undefined rather than truthiness so a
 * legitimately empty list (`[]`) still succeeds.
 */
function unwrap<T>(
  response: { success: boolean; data?: T; error?: { message?: string } },
  action: string,
): T {
  if (!response.success || response.data === undefined || response.data === null) {
    throw new Error(response.error?.message ?? `${action} failed`);
  }
  return response.data;
}

class TripBriefsService {
  async list(filters: { protectedPersonId?: string; status?: string; search?: string } = {}): Promise<TripBriefSummary[]> {
    const params = new URLSearchParams();
    if (filters.protectedPersonId) {params.set('protectedPersonId', filters.protectedPersonId);}
    if (filters.status) {params.set('status', filters.status);}
    if (filters.search) {params.set('search', filters.search);}
    const qs = params.toString();
    const response = await apiClient.get<TripBriefSummary[]>(`${BASE}${qs ? `?${qs}` : ''}`);
    return unwrap(response, 'Loading trip briefs');
  }

  async getById(id: string): Promise<TripBriefRecord> {
    const response = await apiClient.get<TripBriefRecord>(`${BASE}/${id}`);
    return unwrap(response, 'Loading trip brief');
  }

  async create(input: CreateTripBriefInput): Promise<TripBriefRecord> {
    const response = await apiClient.post<TripBriefRecord>(BASE, input);
    return unwrap(response, 'Generating trip brief');
  }

  async update(id: string, updates: UpdateTripBriefInput): Promise<TripBriefRecord | null> {
    const response = await apiClient.put<TripBriefRecord>(`${BASE}/${id}`, updates);
    return response.success && response.data ? response.data : null;
  }

  async remove(id: string): Promise<boolean> {
    const response = await apiClient.delete<{ id: string; deleted: boolean }>(`${BASE}/${id}`);
    return response.success === true;
  }

  async approve(id: string, approverName: string, approverTitle?: string): Promise<TripBriefRecord | null> {
    const response = await apiClient.post<TripBriefRecord>(`${BASE}/${id}/approve`, { approverName, approverTitle });
    return response.success && response.data ? response.data : null;
  }

  async refresh(id: string): Promise<TripBriefRecord | null> {
    const response = await apiClient.post<TripBriefRecord>(`${BASE}/${id}/refresh`, {});
    return response.success && response.data ? response.data : null;
  }

  async rerun(id: string): Promise<TripBriefRecord | null> {
    const response = await apiClient.post<TripBriefRecord>(`${BASE}/${id}/rerun`, {});
    return response.success && response.data ? response.data : null;
  }

  /** Cancel an in-flight live review — aborts running AI calls and settles the brief. */
  async cancel(id: string): Promise<TripBriefRecord | null> {
    const response = await apiClient.post<TripBriefRecord>(`${BASE}/${id}/cancel`, {});
    return response.success && response.data ? response.data : null;
  }

  async regenerateSection(id: string, sectionKey: string): Promise<TripBriefRecord | null> {
    const response = await apiClient.post<TripBriefRecord>(`${BASE}/${id}/sections/${sectionKey}/regenerate`, {});
    return response.success && response.data ? response.data : null;
  }

  async flagSection(id: string, sectionKey: string, needsReview: boolean): Promise<TripBriefRecord | null> {
    const response = await apiClient.post<TripBriefRecord>(`${BASE}/${id}/sections/${sectionKey}/flag`, { needsReview });
    return response.success && response.data ? response.data : null;
  }

  async listVersions(id: string): Promise<TripBriefVersionSummary[]> {
    const response = await apiClient.get<TripBriefVersionSummary[]>(`${BASE}/${id}/versions`);
    return response.success && response.data ? response.data : [];
  }

  async deliver(id: string, recipientEmail: string): Promise<{ delivered: boolean; messageId?: string }> {
    const response = await apiClient.post<{ delivered: boolean; messageId?: string }>(`${BASE}/${id}/deliver`, { recipientEmail });
    if (!response.success || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to deliver brief');
    }
    return response.data;
  }

  async downloadPdf(id: string, filename: string): Promise<void> {
    await apiClient.downloadFile(`${BASE}/${id}/export/pdf`, filename);
  }

  async downloadDocx(id: string, filename: string): Promise<void> {
    await apiClient.downloadFile(`${BASE}/${id}/export/docx`, filename);
  }

  /** VIS-207: read the org-wide travel-risk sensitivity settings. */
  async getSensitivitySettings(): Promise<TravelRiskSettings | null> {
    const response = await apiClient.get<TravelRiskSettings>(SETTINGS_BASE);
    return response.success && response.data ? response.data : null;
  }

  /** VIS-207: update the org-wide travel-risk sensitivity weights. */
  async updateSensitivitySettings(weights: SensitivityWeights): Promise<TravelRiskSettings> {
    const response = await apiClient.put<TravelRiskSettings>(SETTINGS_BASE, weights);
    if (!response.success || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to save sensitivity settings');
    }
    return response.data;
  }
}

export const tripBriefsService = new TripBriefsService();
